/**
 * Lazy Loading Utility for Shopify Theme
 * Loads JavaScript modules on-demand based on:
 * - Intersection Observer (when component enters viewport)
 * - User interaction (hover, click, focus)
 * - Media queries (responsive loading)
 */

class LazyLoader {
	constructor() {
		this.loadedModules = new Set();
		this.pendingModules = new Map();
		this.intersectionObserver = null;
		this.initIntersectionObserver();
	}

	/**
	 * Initialize Intersection Observer for viewport-based loading
	 */
	initIntersectionObserver() {
		if (!("IntersectionObserver" in window)) {
			// Fallback: load immediately if IntersectionObserver not supported
			this.loadAllPendingModules();
			return;
		}

		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const element = entry.target;
						if (element instanceof HTMLElement) {
							const modulePath = element.dataset.lazyModule;
							if (modulePath) {
								this.loadModule(modulePath);
								//@ ts-expect-error - IntersectionObserver is not null here
								this.intersectionObserver.unobserve(element);
							}
						}
					}
				});
			},
			{
				// Start loading slightly before element enters viewport
				rootMargin: "50px 0px",
				threshold: 0.01,
			},
		);
	}

	/**
	 * Load a module by path
	 * @param {string} modulePath - The import path or URL
	 * @returns {Promise<any>} - The loaded module
	 */
	async loadModule(modulePath) {
		// Check if already loaded
		if (this.loadedModules.has(modulePath)) {
			return this.pendingModules.get(modulePath);
		}

		// Mark as loading
		this.loadedModules.add(modulePath);

		try {
			const modulePromise = import(modulePath);
			this.pendingModules.set(modulePath, modulePromise);
			const module = await modulePromise;
			return module;
		} catch (error) {
			console.error(`Failed to load module: ${modulePath}`, error);
			// Remove from loaded set so it can be retried
			this.loadedModules.delete(modulePath);
			throw error;
		}
	}

	/**
	 * Observe an element for lazy loading
	 * @param {HTMLElement} element - Element to observe
	 */
	observe(element) {
		if (!element.dataset.lazyModule) {
			console.warn("Element missing data-lazy-module attribute", element);
			return;
		}

		if (this.intersectionObserver) {
			this.intersectionObserver.observe(element);
		} else {
			// Fallback: load immediately
			this.loadModule(element.dataset.lazyModule);
		}
	}

	/**
	 * Load module on user interaction
	 * @param {HTMLElement} element - Element that triggers loading
	 * @param {string} modulePath - Module to load
	 * @param {string} event - Event type (default: 'mouseenter')
	 */
	loadOnInteraction(element, modulePath, event = "mouseenter") {
		const loadHandler = () => {
			this.loadModule(modulePath);
			element.removeEventListener(event, loadHandler);
		};

		element.addEventListener(event, loadHandler, { once: true, passive: true });
	}

	/**
	 * Load module based on media query
	 * @param {string} modulePath - Module to load
	 * @param {string} mediaQuery - Media query string (e.g., '(min-width: 750px)')
	 */
	loadOnMediaQuery(modulePath, mediaQuery) {
		const mediaQueryList = window.matchMedia(mediaQuery);

		const checkAndLoad = () => {
			if (mediaQueryList.matches) {
				this.loadModule(modulePath);
			}
		};

		// Check immediately
		checkAndLoad();

		// Listen for changes
		mediaQueryList.addEventListener("change", checkAndLoad);
	}

	/**
	 * Load all pending modules immediately (fallback for older browsers)
	 */
	loadAllPendingModules() {
		document.querySelectorAll("[data-lazy-module]").forEach((element) => {
			if (element instanceof HTMLElement) {
				const modulePath = element.dataset.lazyModule;
				if (modulePath) {
					this.loadModule(modulePath);
				}
			}
		});
	}

	/**
	 * Auto-discover and observe all elements with data-lazy-module
	 */
	autoDiscover() {
		document.querySelectorAll("[data-lazy-module]").forEach((element) => {
			if (element instanceof HTMLElement) {
				this.observe(element);
			}
		});
	}
}

// Create global instance
// @ts-ignore - Adding LazyLoader to window object
window.LazyLoader = new LazyLoader();

// Auto-discover on DOMContentLoaded
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		// @ts-ignore
		window.LazyLoader.autoDiscover();
	});
} else {
	// DOM already loaded
	// @ts-ignore
	window.LazyLoader.autoDiscover();
}

// Export for use in other modules
export { LazyLoader };
// @ts-ignore
export default window.LazyLoader;
